import React from 'react';
import {
    reduxForm,
} from 'redux-form';
import { Alert, Spinner } from 'react-bootstrap';
import { withRouter } from 'react-router';
import { Show } from 'Layout';
import { PageStatus, Assets } from 'enums';
import Select from 'react-select';
import { AuthAPI, MasterDataAPI } from "../../API";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { authBasicProfile } from "./auth.actions";
import LanguageToggle from "../Common/LanguageToggle";
import { Grid, Container, TextField, MenuItem, Select as Select2, InputLabel, FormControl } from '@material-ui/core';
import { CountriesAPI } from "../../API/CountriesAPI";
import Language from "../../Languages/Login/content.json";
import { store } from '../../../src/reducers';
import Swal from 'sweetalert2';
import { decryptTkData } from 'Helper/EncryptionDecryption';
import { CURRENCY_SYMBOL } from 'Helper/currency';
import { branding, PORTAL_CODE } from '../../config/portalBranding';
import { JP_POSTCODE_MAP } from '../../config/jpPostcodeMap';
import countryConfig from 'config/countryConfig';
import moment from 'moment/moment';

// 47 Japanese prefectures (standard romanized names, alphabetical).
// Used only for the Japan-specific basic-profile layout. The states API
// is intentionally not trusted here — the JP states data was inherited
// from a UK import and is unreliable; a static array keeps it deterministic.
const JP_PREFECTURES = [
    'Aichi', 'Akita', 'Aomori', 'Chiba', 'Ehime', 'Fukui', 'Fukuoka',
    'Fukushima', 'Gifu', 'Gunma', 'Hiroshima', 'Hokkaido', 'Hyogo',
    'Ibaraki', 'Ishikawa', 'Iwate', 'Kagawa', 'Kagoshima', 'Kanagawa',
    'Kochi', 'Kumamoto', 'Kyoto', 'Mie', 'Miyagi', 'Miyazaki', 'Nagano',
    'Nagasaki', 'Nara', 'Niigata', 'Oita', 'Okayama', 'Okinawa', 'Osaka',
    'Saga', 'Saitama', 'Shiga', 'Shimane', 'Shizuoka', 'Tochigi',
    'Tokushima', 'Tokyo', 'Tottori', 'Toyama', 'Wakayama', 'Yamagata',
    'Yamaguchi', 'Yamanashi',
].map((p) => ({ label: p, value: p }));

const IS_JAPAN_PORTAL = PORTAL_CODE === 'JP';
// Mobile Number is collected ONLY on the India portal. Every other portal
// (JP/UK/MY/PH/SG) hides the field, holds no mobile value, and submits no
// `mobile` key. Gated on the build-time PORTAL_CODE.
const IS_INDIA_PORTAL = PORTAL_CODE === 'IN';

export type FormValue = {
    "firstName": string,
    "lastName": string,
    "description": string,
    "features": any,
    "images": any,
    "logo": any,
};

type State = {
    status: string,
    error: string | null,
    firstName: string,
    lastName: string,
    mobile: any,
    gender: any,
    dateOfBirth: any,
    selectedCountryOption: any,
    selectedStateOption: any,
    selectedCityOption: any,
    countryOptions: any,
    country: any,
    addressLine1: any,
    addressLine2: any,
    state: any,
    city: any,
    pinCode: any,
    referralSource: any,
    countries: any,
    states: any,
    cities: any
};

class BasicProfile extends React.Component<any, any> {
    constructor(props) {
        super(props);
        this.state = {
            status: PageStatus.None,
            error: null,
            firstName: '',
            lastName: '',
            // Japan layout uses a single "Full Name" input that is split into
            // firstName/lastName on submit; this backs that input.
            fullName: '',
            // Mobile is collected ONLY on the India portal (IS_INDIA_PORTAL).
            // On every other portal the field is not rendered, this value stays
            // '', and no `mobile` key is sent on submit. The backend
            // update-basic-profile path does not validate phone
            // (mobileValidationMiddleware removed 2026-05-15; updateValidator has
            // no mobile rule), so an absent `mobile` is accepted for non-IN.
            mobile: '',
            gender: 'Male',
            dateOfBirth: '',
            selectedCountryOption: null,
            selectedStateOption: null,
            selectedCityOption: null,
            countryOptions: [],
            country: PORTAL_CODE,
            addressLine1: '',
            addressLine2: '',
            state: '',
            city: '',
            pinCode: '',
            // Referral Source is a visible, required dropdown on the JP layout
            // (same as non-JP) — no silent default; the user must pick one.
            referralSource: '',
            countries: [],
            states: [],
            cities: [],
            errors: {},
        };
    }

    // Active language for this pre-auth page. Redux `adminUser.language` is
    // null until the user picks from the language dropdown; until then fall
    // back to the portal's own language (JP portal => 'jp') so the page is
    // localized on first load. Derived per-render so a dropdown change
    // re-localizes the whole page immediately.
    getLang = () => this.props.language || (IS_JAPAN_PORTAL ? 'jp' : 'en');

    // The localized content block for the active language.
    getPageContent = () => {
        const lang = this.getLang();
        return lang === 'jp' ? Language.BasicProfileJapanese
             : lang === 'hi' ? Language.BasicProfileHindi
             : Language.BasicProfileEnglish;
    };

    validateForm = () => {
        let errors: any = {};
        const { t } = this.props;
        if (IS_JAPAN_PORTAL) {
            // Japan layout: Full Name, Date Of Birth, Gender, Address Line,
            // City, Prefecture, Postcode, Referral Source. mobile/country are
            // silent defaults and are not validated here.
            // Messages go through t() so they follow the page language toggle
            // (en/jp/hi) — the IS_JAPAN_PORTAL branch is a layout choice, not
            // a language choice.
            if (!this.state.fullName.trim()) { errors.fullName = t('auth.basicProfileJp.fullNameRequired'); }
            if (!this.state.gender) { errors.gender = t('auth.basicProfileJp.genderRequired'); }
            if (!this.state.dateOfBirth) { errors.dateOfBirth = t('auth.basicProfileJp.dateOfBirthRequired'); }
            if (!this.state.addressLine1.trim()) { errors.addressLine1 = t('auth.basicProfileJp.addressRequired'); }
            if (!this.state.city.trim()) { errors.city = t('auth.basicProfileJp.cityRequired'); }
            if (!this.state.state) { errors.state = t('auth.basicProfileJp.stateRequired'); }
            if (!/^\d{3}-\d{4}$/.test(this.state.pinCode)) {
                errors.pinCode = t('auth.basicProfileJp.postcodeFormatError');
            } else if (!JP_POSTCODE_MAP[this.state.pinCode.replace(/[\s-]/g, '').slice(0, 3)]) {
                errors.pinCode = t('auth.basicProfileJp.postcodeInvalidError');
            }
            if (!this.state.referralSource) { errors.referralSource = t('auth.basicProfileJp.referralSourceRequired'); }
            if (!this.checkValidDateOfBirth()) { errors.dateOfBirth = t('auth.basicProfileJp.ageRestrictionError'); }
            this.setState({ errors });
            return Object.keys(errors).length === 0;
        }
        if (!this.state.firstName.trim()) { errors.firstName = "First name is required"; }
        if (!this.state.lastName.trim()) { errors.lastName = "Last name is required"; }
        // ===== REMOVED 2026-05-15 — phone eliminated from basic-profile =====
        // `mobile` is no longer collected on any portal; it is not required
        // and no phone-format validation runs on it.
        // if (!this.state.mobile.trim()) { errors.mobile = "Mobile number is required"; }
        if (!this.state.gender) { errors.gender = "Gender is required"; }
        if (!this.state.dateOfBirth) { errors.dateOfBirth = "Date of birth is required"; }
        if (!this.state.pinCode.trim()) { errors.pinCode = "Pin code is required"; }
        if (!this.state.addressLine1.trim()) { errors.addressLine1 = "Address Line 1 is required"; }
        if (!this.state.referralSource) { errors.referralSource = "Referral source is required"; }
        if (!this.checkValidDateOfBirth()) { errors.dateOfBirth = this.props.t('auth.ageRestrictionError'); }
        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    checkValidDateOfBirth = () => {
        const enteredDate = this.state.dateOfBirth;
        const currentDate = new Date();
        const minAgeDate = new Date(currentDate.getFullYear() - 100, currentDate.getMonth(), currentDate.getDate());
        const maxAgeDate = new Date(currentDate.getFullYear() - 16, currentDate.getMonth(), currentDate.getDate());
        const selectedDate: any = new Date(enteredDate);
        const enteredTimestamp = selectedDate.getTime();
        const minAgeTimestamp = minAgeDate.getTime();
        const maxAgeTimestamp = maxAgeDate.getTime();
        if (!isNaN(selectedDate) && enteredTimestamp >= minAgeTimestamp && enteredTimestamp <= maxAgeTimestamp) {
            return true;
        }
        return false;
    }


    async componentDidMount() {
        // India-only: pre-fill the Mobile field from the phone number already
        // verified during signup (Redux `adminUser.phoneNumber`, mapped to
        // this.props.phoneNumber via mapStateToProps below). Non-IN portals
        // never render the field, so this branch is a no-op for them. The
        // empty-fallback guard lets dev/test cases without a stored phone
        // number still allow manual entry.
        if (IS_INDIA_PORTAL && this.props.phoneNumber) {
            this.setState({ mobile: String(this.props.phoneNumber) });
        }
        await this.fetchCountryList()
        this.fetchAllStates()
    }

    formValues() {
        return {
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            // Mobile key included only on the India portal; omitted elsewhere.
            ...(IS_INDIA_PORTAL ? { mobile: this.state.mobile } : {}),
            gender: this.state.gender,
            dateOfBirth: this.state.dateOfBirth,
            country: this.state.country,
            addressLine1: this.state.addressLine1,
            addressLine2: this.state.addressLine2 || 'NA',
            state: this.state.state,
            city: this.state.city,
            pinCode: this.state.pinCode,
            referralSource: this.state.referralSource,
            acceptTerms: true

        };
    }


    onSubmit() {
        if (this.props.userId) {
            return this.createProfile();
        }
    }

    createProfile() {
        if (this.props.userId) {
            const valuesIn = this.formValues();
            if (!this.validateForm()) { return; }
            return Promise.resolve()
                .then(() => this.setState({ status: PageStatus.Submitting }))
                .then(() => AuthAPI.createBasicProfile(valuesIn, this.props.userId, this.props.language))
                .then(async (profile) => {
                    if (profile.loginCoupon) {
                        // profile.rewardCorporation is set by the backend on the
                        // BasicProfile response so the modal knows which brand to show.
                        const isChaiPoint = profile.rewardCorporation === 'ChaiPoint';
                        let objSet;
                        if (isChaiPoint) {
                            // ── ChaiPoint success modal ───────────────────────────────
                            objSet = {
                                en: `
            <div style="text-align: left;">
              Thank you for signing up on ${countryConfig.brandName}! <br><br>

              Here's your ${CURRENCY_SYMBOL}100 ChaiPoint voucher.<br><br>

              <b>Voucher Code:</b> ${decryptTkData(profile.loginCoupon)}<br>
              <b>Valid till:</b> ${profile.couponExpiryAt ? moment(profile.couponExpiryAt).format('DD-MM-YYYY') : ''}<br>
              Voucher valid only at this ChaiPoint outlet.<br><br>

              Show this code at the ChaiPoint counter and enjoy your tea on us.<br><br>

              <b>Complete your profile and earn ${CURRENCY_SYMBOL}100 more!</b>
              </div>
            `,
                                hi: `
            <div style="text-align: left;">
              ${countryConfig.brandName} पर साइन-अप करने के लिए धन्यवाद! <br><br>

              यह रहा आपका ${CURRENCY_SYMBOL}100 का चाईपॉइंट वाउचर<br><br>

              <b>वाउचर कोड:</b> ${decryptTkData(profile.loginCoupon)}<br>
              <b>मान्य है:</b> ${profile.couponExpiryAt}<br>
              यह वाउचर सिर्फ इसी चाईपॉइंट आउटलेट पर मान्य है।<br><br>

              चाईपॉइंट काउंटर पर यह कोड दिखाएँ और हमारी तरफ़ से चाय का मज़ा लें।<br><br>

              <b>अपनी प्रोफ़ाइल पूरी करें और ${CURRENCY_SYMBOL}100 और कमाएँ!</b>
              </div>
            `,
                                jp: `
            <div style="text-align: left;">
              ${countryConfig.brandName}にご登録いただきありがとうございます! <br><br>

              こちらが${CURRENCY_SYMBOL}100のChaiPointクーポンです。 <br><br>

              <b>クーポンコード: </b> ${decryptTkData(profile.loginCoupon)}<br>
              <b>有効期限: </b> ${profile.couponExpiryAt}<br>
              このクーポンはこのChaiPoint店舗でのみ有効です。 <br><br>

              ChaiPointのカウンターでこのコードを提示して、お茶をお楽しみください。 <br><br>

              <b>プロフィールを完成させて、さらに${CURRENCY_SYMBOL}100を獲得しましょう! </b>
              </div>
            `,
                            };
                        } else {
                            // ── Barista success modal (unchanged) ────────────────────
                            objSet = {
                                en: `
            <div style="text-align: left;">
              Thank you for signing up on ${countryConfig.brandName}! <br><br>

              Here's your ${CURRENCY_SYMBOL}100 Barista voucher.<br><br>

              <b>Voucher Code:</b> ${decryptTkData(profile.loginCoupon)}<br>
              <b>Valid till:</b> ${profile.couponExpiryAt}<br>
              Voucher valid only at this Barista outlet.<br><br>


              Show this code at the Barista counter and enjoy your coffee on us.<br><br>

              <b>Complete your profile and earn ${CURRENCY_SYMBOL}50 more!</b>
              </div>
            `,
                                hi: `
            <div style="text-align: left;">
              ${countryConfig.brandName} पर साइन-अप करने के लिए धन्यवाद! <br><br>

              यह रहा आपका ${CURRENCY_SYMBOL}100 का बरिस्ता वाउचर<br><br>

              <b>वाउचर कोड:</b> ${decryptTkData(profile.loginCoupon)}<br>
              <b>मान्य है:</b> ${profile.couponExpiryAt}<br>
              यह वाउचर सिर्फ इसी बरिस्ता आउटलेट पर मान्य है।<br><br>

              बरिस्ता काउंटर पर यह कोड दिखाएँ और हमारी तरफ़ से कॉफी का मज़ा लें।<br><br>

              <b>अपनी प्रोफ़ाइल पूरी करें और ${CURRENCY_SYMBOL}50 और कमाएँ!</b>
              </div>
            `,
                                jp: `
            <div style="text-align: left;">
              ${countryConfig.brandName}にご登録いただきありがとうございます! <br><br>

              こちらが${CURRENCY_SYMBOL}100のバリスタクーポンです。 <br><br>

              <b>クーポンコード: </b> ${decryptTkData(profile.loginCoupon)}<br>
              <b>有効期限: </b> ${profile.couponExpiryAt}<br>
              このクーポンはこのバリスタ店舗でのみ有効です。 <br><br>

              バリスタのカウンターでこのコードを提示して、コーヒーをお楽しみください。 <br><br>

              <b>プロフィールを完成させて、さらに${CURRENCY_SYMBOL}50を獲得しましょう! </b>
              </div>
            `,
                            };
                        }
                        await Swal.fire({
                            title: "Success!",
                            icon: "success",
                            html: objSet[this.props.language || 'en'],
                            confirmButtonText: "OK",
                        });

                    }
                    this.setState({ status: PageStatus.Submitted });
                    const state = store.getState();
                    const uniquetokennew = state.adminUser.adminUser.apiToken;
                    return this.props.authBasicProfile(this.props.history, this.props.userId, profile.role, uniquetokennew, profile.loginRewardType)
                })
                .catch((error) => {
                    this.setState({ status: PageStatus.Error, error: error.message });
                });
        }
    }


    fetchCountryList(): Promise<void> {
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => MasterDataAPI.countryList('10'))
            .then((countries) => {
                const options = countries.map(country => ({
                    label: this.props.language === 'hi' ? country.hindi : country.name,
                    value: country.shortName
                }));
                // Match by value (country.title) which equals PORTAL_CODE for the 6 panel countries.
                // Fallback to options[0] only if the master-data list is missing the portal's row.
                const portalOption = options.find(opt => opt.value === PORTAL_CODE) || options[0];
                this.setState({
                    countries: options,
                    status: PageStatus.Loaded,
                    ...(portalOption ? { country: portalOption.value, selectedCountryOption: portalOption } : {}),
                });
            })
            .catch((error) => {
                this.setState({ error: error.message, status: PageStatus.Error });
            });
    }


    fetchAllStates(): Promise<void> {
        return CountriesAPI.getAllStates(1000)
            .then((statesList) => {
                const options = statesList.map(s => ({
                    label: this.props.language === 'hi' ? s.hindi : s.name,
                    value: s.name,
                    id: s.id,
                }));
                options.sort((a, b) => a.label < b.label ? -1 : a.label > b.label ? 1 : 0);
                this.setState({ states: options });
            })
            .catch((error) => {
                this.setState({ error: error.message });
            });
    }

    fetchCitiesByStateId(stateId): Promise<void> {
        return CountriesAPI.getUniqueDistrictByStateIds({ stateIds: [stateId] })
            .then((districts) => {
                const options = districts
                    .filter(d => d.segment)
                    .map(d => ({
                        label: d.segment,
                        value: d.segment,
                    }));
                options.sort((a, b) => a.label < b.label ? -1 : a.label > b.label ? 1 : 0);
                this.setState({ cities: options });
            })
            .catch((error) => {
                this.setState({ error: error.message });
            });
    }

    handleCountryChange = async (selectedCountryOption) => {
        this.setState({
            country: selectedCountryOption.value,
            selectedCountryOption,
            states: [], cities: [],
            selectedStateOption: null, selectedCityOption: null,
            state: '', city: '',
        });
        this.fetchAllStates();
    };

    handleStateChange = async (selectedStateOption) => {
        this.setState({
            state: selectedStateOption.value,
            selectedStateOption,
            cities: [], selectedCityOption: null, city: '',
        });
        if (selectedStateOption.id) {
            this.fetchCitiesByStateId(selectedStateOption.id);
        }
    };

    handleCityChange = async (selectedCityOption) => {
        this.setState({ city: selectedCityOption.value, selectedCityOption });
    };

    fetchStateAndCitiesByZipCode(code): Promise<void> {
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loaded }))
            .then(() => CountriesAPI.getAllCitiesAndStateBasedOnZipCode(1000, code))
            .then((states: any) => {
                if (states.state && states.state.length > 0) {
                    const stateName = states.state[0].title || states.state[0].name;
                    const stateMatch = this.state.states.find((s: any) => s.value === stateName);
                    if (stateMatch) {
                        this.setState({ state: stateMatch.value, selectedStateOption: stateMatch });
                        this.fetchCitiesByStateId(stateMatch.id);
                    }
                }
            })
            .catch((error) => {
                this.setState({ error: error.message, status: PageStatus.Error });
            });
    }


    handleDateOfBirthChange(e) {
        const enteredDate = e.target.value;
        this.setState((prev) => ({ dateOfBirth: enteredDate, errors: { ...prev.errors, ["dateOfBirth"]: "" } }));
    }


    handleZipCodeChange = (event) => {
        const { value } = event.target;
        if (!/^\d*$/.test(value) || value.length > 6) { return; }
        this.setState((prev) => ({ pinCode: value, errors: { ...prev.errors, ["pinCode"]: "" } }));
        if (value.length === 6) {
            this.fetchStateAndCitiesByZipCode(value)
        } else {
            this.setState({ state: '', selectedStateOption: null });
            this.setState({ city: '', selectedCityOption: null });
        }
    };

    handleInputChange = (e, regex) => {
        const { value } = e.target;
        if (regex.test(value) || value === '') {
            this.setState((prev) => ({ [e.target.name]: value, errors: { ...prev.errors, [e.target.name]: "" } }));
        }
    };

    // --- Japan-specific handlers ---------------------------------------

    // Full Name -> split on first space into firstName + lastName.
    // No space (or empty last part): lastName falls back to a single space.
    // The backend updateValidator requires lastName length >= 1; a single
    // space satisfies that without distorting the name (same approach as
    // the JP `mobile: ' '` default). Trailing space is harmless on submit.
    handleFullNameChange = (e) => {
        const value = e.target.value;
        const trimmed = value.trim();
        const idx = trimmed.indexOf(' ');
        const firstName = idx === -1 ? trimmed : trimmed.slice(0, idx);
        const lastNameRaw = idx === -1 ? '' : trimmed.slice(idx + 1).trim();
        const lastName = lastNameRaw === '' ? ' ' : lastNameRaw;
        this.setState((prev) => ({
            fullName: value,
            firstName,
            lastName,
            errors: { ...prev.errors, fullName: "" },
        }));
    };

    handleJpPrefectureChange = (selectedStateOption) => {
        // Japan prefecture maps to the `state` column. Free-text city is used
        // on the JP layout, so no dependent city lookup is triggered here.
        this.setState((prev) => ({
            state: selectedStateOption ? selectedStateOption.value : '',
            selectedStateOption,
            errors: { ...prev.errors, state: "" },
        }));
    };

    // Guard against repeating the same lookup — mirrors `lastLookupPostcode`
    // in the reference implementation (dxpanel/JP/script/script.js).
    lastLookupPostcode = '';

    // Postcode -> pinCode column. Accepts digits and a single hyphen,
    // formats toward NNN-NNNN; full validation happens in validateForm.
    // When a complete NNN-NNNN value is entered, triggers the JP postcode
    // autocomplete to fill Prefecture + City.
    handleJpPostcodeChange = (e) => {
        let value = e.target.value.replace(/[^\d-]/g, '');
        const digits = value.replace(/-/g, '').slice(0, 7);
        value = digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
        this.setState((prev) => ({ pinCode: value, errors: { ...prev.errors, pinCode: "" } }));
        if (/^\d{3}-\d{4}$/.test(value)) {
            this.lookupJpPostcode(digits);
        } else {
            // Reset the guard so re-entering a complete postcode looks up again.
            this.lastLookupPostcode = '';
        }
    };

    // Japan postcode autocomplete — synchronous in-memory lookup against
    // JP_POSTCODE_MAP (ported from dxpanel/JP/get_city_by_postcode.php).
    // Looks up the first 3 digits and auto-fills Prefecture + City.
    // Silent on miss: fields are left untouched for manual entry, and
    // submission is never blocked.
    lookupJpPostcode = (postcode) => {
        const normalized = String(postcode || '').replace(/[\s-]/g, '');
        if (!normalized || normalized === this.lastLookupPostcode) {
            return;
        }
        this.lastLookupPostcode = normalized;

        const hit = JP_POSTCODE_MAP[normalized.slice(0, 3)];
        if (hit) {
            this.setState((prev: any) => ({
                city: hit.city,
                state: hit.prefecture,
                selectedStateOption: { label: hit.prefecture, value: hit.prefecture },
                errors: { ...prev.errors, city: "", state: "" },
            }));
        }
        // Miss: do nothing — silent, manual entry remains usable.
    };

    // Pick the referral-option label for the active language. 'jp' and 'hi'
    // each have their own translated label in Language.NewReferrals; fall
    // back to English when the language has no translation for that entry.
    referralLabel = (referral: any) => {
        const lang = this.getLang();
        if (lang === 'jp') { return referral.label.jp || referral.label.en; }
        if (lang === 'hi') { return referral.label.hi || referral.label.en; }
        return referral.label.en;
    };


    render() {
        const { selectedCountryOption, selectedStateOption, selectedCityOption } = this.state
        const { t } = this.props;
        // "Ad at Barista" is an India-specific referral source — Barista is an
        // Indian coffee chain. Hide it on every non-IN portal (JP included);
        // all other options stay visible everywhere.
        const referralsOptions = Language.NewReferrals.filter(
            (referral: any) => (referral.value !== 'Ad at Barista' && referral.value !== 'Ad at Chai Point') || countryConfig.code === 'IN',
        )
        const pageContent = this.getPageContent();
        return (
            <Container maxWidth="lg">
                {/* Language switcher — the shared LanguageToggle pill control,
                    same as the post-login navbar (AdminNavbarLinks.js). Gives
                    this pre-auth page a real `language` value so the whole
                    page localizes. Renders nothing on en-only portals. */}
                <div style={{ textAlign: 'right', paddingTop: '0.75rem' }}>
                    <LanguageToggle />
                </div>
                <Grid container justify="center" alignItems="center">
                    <Grid item>
                        <img
                            src={branding.logo || Assets.Logo2}
                            alt="Logo"
                            style={{
                                width: '33%',
                                height: 'auto',
                                display: 'block',
                                margin: '0 auto',
                                paddingTop: '2.5rem',
                                paddingBottom: '2.5rem',
                            }}
                        />
                    </Grid>
                </Grid>
                <div className='text-center mb-3'>{pageContent.title}</div>
                <Show when={this.state.status === PageStatus.Loading}>
                    <div className="d-flex justify-content-center w-100 p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                </Show>

                <Alert variant="danger" show={this.state.status === PageStatus.Error}>
                    {this.state.error}
                </Alert>
                <form
                    noValidate
                    onSubmit={this.props.handleSubmit((event) => this.onSubmit())}
                    className="mt-4"
                >

                    {IS_JAPAN_PORTAL ? (
                    <>
                        {/* ===== Japan-specific basic-profile layout ===== */}
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <TextField
                                    id="fullName"
                                    label={pageContent.jpFields.fullName}
                                    className="form-control"
                                    name="fullName"
                                    onChange={this.handleFullNameChange}
                                    value={this.state.fullName}
                                    error={!!this.state.errors.fullName}
                                    helperText={this.state.errors.fullName}
                                    required
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    id="email"
                                    label={pageContent.jpFields.email}
                                    className="form-control"
                                    name="email"
                                    value={this.props.email || ''}
                                    InputProps={{ readOnly: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    id="dateOfBirthJp"
                                    label={pageContent.jpFields.dateOfBirth}
                                    type="date"
                                    defaultValue={null}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => this.handleDateOfBirthChange(e)}
                                    error={!!this.state.errors.dateOfBirth}
                                    helperText={this.state.errors.dateOfBirth}
                                    required
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <br />

                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <FormControl style={{ width: '100%' }}>
                                    <InputLabel htmlFor="genderJp">{pageContent.jpFields.gender}</InputLabel>
                                    <Select2
                                        value={this.state.gender}
                                        onChange={(e) => this.setState({ gender: e.target.value })}
                                        inputProps={{ name: 'gender', id: 'genderJp' }}
                                        required
                                    >
                                        <MenuItem value="" disabled>{pageContent.jpFields.genderSelect}</MenuItem>
                                        <MenuItem value="Male">{pageContent.jpFields.genderMale}</MenuItem>
                                        <MenuItem value="Female">{pageContent.jpFields.genderFemale}</MenuItem>
                                        <MenuItem value="Other">{pageContent.jpFields.genderOther}</MenuItem>
                                    </Select2>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    id="addressLineJp"
                                    label={pageContent.jpFields.address}
                                    className="form-control"
                                    name="addressLine1"
                                    value={this.state.addressLine1}
                                    onChange={(e) => this.setState((prev: any) => ({ addressLine1: e.target.value, errors: { ...prev.errors, ["addressLine1"]: "" } }))}
                                    error={!!this.state.errors.addressLine1}
                                    helperText={this.state.errors.addressLine1}
                                    required
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    id="cityJp"
                                    label={pageContent.jpFields.city}
                                    className="form-control"
                                    name="city"
                                    value={this.state.city}
                                    onChange={(e) => this.setState((prev: any) => ({ city: e.target.value, errors: { ...prev.errors, ["city"]: "" } }))}
                                    error={!!this.state.errors.city}
                                    helperText={this.state.errors.city}
                                    required
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <br />

                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Select
                                    name='prefecture'
                                    id='prefecture'
                                    onChange={this.handleJpPrefectureChange}
                                    value={selectedStateOption}
                                    required
                                    placeholder={pageContent.jpFields.prefecture}
                                    options={JP_PREFECTURES}
                                />
                                {this.state.errors.state && (
                                    <p className="text-danger" style={{ fontSize: "12px" }}>
                                        {this.state.errors.state}
                                    </p>
                                )}
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    id="postcodeJp"
                                    label={pageContent.jpFields.postcode}
                                    className="form-control"
                                    name="pinCode"
                                    value={this.state.pinCode}
                                    onChange={this.handleJpPostcodeChange}
                                    error={!!this.state.errors.pinCode}
                                    helperText={this.state.errors.pinCode || pageContent.jpFields.postcodeHelp}
                                    required
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <FormControl style={{ width: '100%' }} error={!!this.state.errors.referralSource}>
                                    <InputLabel htmlFor="referralSourceJp">{pageContent.jpFields.referralSource}</InputLabel>
                                    <Select2
                                        value={this.state.referralSource}
                                        onChange={(e) => this.setState((prev: any) => ({ referralSource: e.target.value, errors: { ...prev.errors, ["referralSource"]: "" } }))}
                                        inputProps={{ name: 'referralSource', id: 'referralSourceJp' }}
                                        required
                                    >
                                        <MenuItem value="" disabled>{pageContent.jpFields.referralSelect}</MenuItem>
                                        {referralsOptions.map((referral) => (
                                            <MenuItem key={referral.value} value={referral.value}>
                                                {this.referralLabel(referral)}
                                            </MenuItem>
                                        ))}
                                    </Select2>
                                </FormControl>
                                {this.state.errors.referralSource && (
                                    <p className="text-danger" style={{ fontSize: "12px" }}>
                                        {this.state.errors.referralSource}
                                    </p>
                                )}
                            </Grid>
                        </Grid>
                    </>
                    ) : (
                    <>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <TextField
                                id="title"
                                label={pageContent.items[2].title}
                                className="form-control"
                                name="firstName"
                                onChange={(e) => this.handleInputChange(e, /^[a-zA-Z]*$/)}
                                value={this.state.firstName}
                                placeholder={pageContent.items[1].title}
                                error={!!this.state.errors.firstName}
                                required
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                id="lastName"
                                label={pageContent.items[3].title}
                                className="form-control"
                                name="lastName"
                                onChange={(e) => this.handleInputChange(e, /^[a-zA-Z]*$/)}
                                value={this.state.lastName}
                                placeholder={pageContent.items[1].title}
                                error={!!this.state.errors.lastName}
                                required
                            />
                        </Grid>
                        {/* Mobile Number — India portal ONLY. Hidden on JP (separate
                            layout) and on UK/MY/PH/SG (this non-JP layout, gated below). */}
                        {IS_INDIA_PORTAL && (
                            <Grid item xs={4}>
                                {/* India-only: when pre-filled from the signup-verified
                                    phone number, lock the field (already verified at
                                    signup, no manual override). Falls back to editable
                                    when the props/state value is empty so dev/test cases
                                    without a stored number can still manually enter one.
                                    Disabled inputs still submit through React controlled
                                    state — formValues() reads this.state.mobile. */}
                                <TextField
                                    id="mobile"
                                    label={pageContent.items[4].title}
                                    className="form-control"
                                    name="mobile"
                                    onChange={(e) => this.handleInputChange(e, /^\d*$/)}
                                    value={this.state.mobile}
                                    placeholder={pageContent.items[1].title}
                                    error={!!this.state.errors.mobile}
                                    required
                                    disabled={IS_INDIA_PORTAL && !!this.props.phoneNumber && !!this.state.mobile}
                                    InputProps={{ readOnly: IS_INDIA_PORTAL && !!this.props.phoneNumber && !!this.state.mobile }}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <br />

                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <FormControl style={{ width: '100%' }}>
                                <InputLabel htmlFor="gender">{pageContent.items[5].title}</InputLabel>
                                <Select2
                                    value={this.state.gender}
                                    onChange={(e) => this.setState({ gender: e.target.value })}
                                    inputProps={{
                                        name: 'gender',
                                        id: 'gender',
                                    }}
                                    required
                                >
                                    <MenuItem value="" disabled>--Choose--</MenuItem>
                                    <MenuItem value="Male">{t('common.genderMale')}</MenuItem>
                                    <MenuItem value="Female">{t('common.genderFemale')}</MenuItem>
                                    <MenuItem value="Other">{t('common.genderOther')}</MenuItem>
                                </Select2>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                id="description"
                                label={pageContent.items[6].title}
                                type="date"
                                defaultValue={null}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                onChange={(e) => this.handleDateOfBirthChange(e)}
                                error={!!this.state.errors.dateOfBirth}
                                helperText={this.state.errors.dateOfBirth}
                                required
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                id="pinCode"
                                label={pageContent.items[7].title}
                                value={this.state.pinCode}
                                onChange={(e) => this.handleZipCodeChange(e)}
                                placeholder={pageContent.items[1].title}
                                error={!!this.state.errors.pinCode}
                                required
                            />
                        </Grid>
                    </Grid>
                    <br />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                id="addressLine1"
                                label={pageContent.items[8].title}
                                className="form-control"
                                name="addressLine1"
                                value={this.state.addressLine1}
                                onChange={(e) => this.setState((prev: any) => ({ addressLine1: e.target.value, errors: { ...prev.errors, ["addressLine1"]: "" } }))}
                                placeholder={pageContent.items[1].title}
                                error={!!this.state.errors.addressLine1}
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="addressLine2"
                                label={pageContent.items[9].title}
                                className="form-control"
                                name="addressLine2"
                                value={this.state.addressLine2}
                                onChange={(e) => this.setState({ addressLine2: e.target.value })}
                                placeholder={pageContent.items[1].title}
                            />
                        </Grid>
                    </Grid>
                    <br />

                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Select
                                name='countryTitle'
                                id='countryTitle'
                                onChange={this.handleCountryChange}
                                value={selectedCountryOption}
                                required
                                placeholder={pageContent.items[10].title}
                                options={this.state.countries}
                                isDisabled={true}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Select
                                name='state'
                                id='state'
                                onChange={this.handleStateChange}
                                value={selectedStateOption}
                                required
                                placeholder={pageContent.items[11].title}
                                options={this.state.states}
                            />
                            {this.state.errors.state && (
                                <p className="text-danger" style={{ fontSize: "12px" }}>
                                    {this.state.errors.state}
                                </p>
                            )}
                        </Grid>
                        <Grid item xs={4}>
                            <Select
                                name='city'
                                id='city'
                                onChange={this.handleCityChange}
                                value={selectedCityOption}
                                placeholder={pageContent.items[12].title}
                                required
                                options={this.state.cities}
                            />
                            {this.state.errors.city && (
                                <p className="text-danger" style={{ fontSize: "12px" }}>
                                    {this.state.errors.city}
                                </p>
                            )}
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <label htmlFor='referralSource'>{pageContent.items[13].title + "*"}</label>
                        <select
                            style={{
                                width: '100%',
                                display: 'block',
                                height: '40px',
                                lineHeight: '1.5',
                                color: '#495057',
                                backgroundColor: '#fff',
                                backgroundClip: 'padding-box',
                                border: `1px solid ${this.state.errors.referralSource ? 'red' : '#ced4da'}`,
                                borderRadius: '5px',
                                transition:
                                    'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                            }}
                            name='referralSource'
                            id='referralSource'
                            value={this.state.referralSource}
                            required
                            onChange={(e) =>
                                this.setState((prev) => ({ referralSource: e.target.value, errors: { ...prev.errors, ["referralSource"]: "" } }))
                            }
                        >
                            <option value="" className="">{t('auth.selectReferralSource')}</option>
                            {referralsOptions.map((referral) => (
                                <option key={referral.value} value={referral.value} className="">{this.referralLabel(referral)}</option>
                            ))}
                        </select>
                        {this.state.errors.referralSource && (
                            <p className="text-danger" style={{ fontSize: "12px" }}>
                                {this.state.errors.referralSource}
                            </p>
                        )}
                    </Grid>
                    </>
                    )}
                    <hr />
                    <div className="mt-2 d-flex justify-content-center">
                        <button
                            type="submit"
                            disabled={false}
                            className="btn btn-primary mr-3"
                        >
                            {pageContent.items[14].title}
                        </button>

                        <Show when={this.state.status === PageStatus.Submitting}>
                            <Spinner animation="border" variant="primary" />
                        </Show>
                    </div>
                </form>
            </Container>
        );
    }
}

const basicProfileFormRedux = reduxForm<FormValue, any>({
    form: 'profileForm',
})(withTranslation()(BasicProfile));

const mapStateToProps = (state: { adminUser: { adminUser: { phoneNumber: any, email: any, language: any, userId: any; token: any; loading: any; error: any; role: any }; }; }) => {
    return {
        userId: state.adminUser.adminUser.userId,
        role: state.adminUser.adminUser.role,
        phoneNumber: state.adminUser.adminUser.phoneNumber,
        language: state.adminUser.adminUser.language,
        email: state.adminUser.adminUser.email,
        isLoading: state.adminUser.adminUser.loading,
        error: state.adminUser.adminUser.error,
    };
};

const BasicProfileWithRouter = withRouter(connect(mapStateToProps, { authBasicProfile })(basicProfileFormRedux));

export { BasicProfileWithRouter as BasicProfile };
